import * as React from 'react';

import { Box, Collapse } from '@mui/material';

import { Add } from '@mui/icons-material';

import { observer } from 'mobx-react';

import { Button } from 'components/Button';

import {
  AssignmentRulePipelineResponseType,
  AssignmentRuleResponseType,
} from 'utils/api/modules/entities/entities.types';
import UserAndGroupPicker, { PickerItemKind, PickerValue } from 'components/SharedFormComponents/UserAndGroupPicker';

import { useStore } from 'utils/hooks/store';

import { AssignmentRuleTypeEnum } from 'stores/AssignmentRules/AssignmentRules';

import { isAssignmentRuleResponseType, MANAGER } from 'stores/AssignmentRules/helpers';

import PipelinePicker from 'containers/Events/components/PipelinesPicker';

import { SectionContainer, ConditionContainer, ConditionText, FieldLabel } from '../styles';
import SectionHeader from '../Section/SectionHeader';
import SectionTooltipAction from '../Section/SectionTooltip';
import AssignmentCondition from '../AssignmentCondition';
import { AssignmentRuleContainer } from './styles';
import GroupLicenceNotice from './GroupLicenceNotice';

const AssignmentRule: React.FC<{
  typeOfAssignmentRule: AssignmentRuleTypeEnum;
  rule: AssignmentRuleResponseType | AssignmentRulePipelineResponseType;
}> = ({ typeOfAssignmentRule, rule }): React.ReactElement => {
  const { assignmentRules } = useStore();
  const [expanded, setExpanded] = React.useState(true);

  const addCondition = (): void => assignmentRules.addConditionGeneric(rule.uuid);
  // Group targets are disabled here on purpose until the permissions engine unblocks them.
  const groupTargetsEnabled = false;

  const collaboratorPickerValue: PickerValue[] = isAssignmentRuleResponseType(rule)
    ? [
        ...(rule.targetUsers || []).map(({ uuid, name }): PickerValue => ({ kind: PickerItemKind.User, uuid, name })),
        ...(rule.targetGroups || []).map(
          ({ uuid, name, memberCount }): PickerValue => ({ kind: PickerItemKind.Group, uuid, name, memberCount })
        ),
      ]
    : [];

  return (
    <SectionTooltipAction addCondition={addCondition}>
      <SectionContainer>
        <SectionHeader setExpanded={setExpanded} expanded={expanded} ruleId={rule.uuid} />
        <Collapse in={expanded}>
          <AssignmentRuleContainer $expanded={expanded}>
            {rule.conditions.map((condition, index) => (
              <AssignmentCondition key={index} rule={rule} index={index} condition={condition} />
            ))}
            <Button
              size="small"
              variant="text"
              onClick={addCondition}
              startIcon={<Add sx={{ width: '16px', height: '16px' }} />}
            >
              Add condition
            </Button>
            <ConditionContainer>
              <ConditionText>THEN</ConditionText>
              <Box sx={{ flex: 1 }}>
                {typeOfAssignmentRule === AssignmentRuleTypeEnum.Collaborators ? (
                  <>
                    <FieldLabel>Add collaborators</FieldLabel>
                    <UserAndGroupPicker
                      data-testid={`assignment-rule-collaborators-${rule.uuid}`}
                      enableGroups={groupTargetsEnabled}
                      // oxlint-disable-next-line jsx-a11y/aria-role -- role is UserAndGroupPicker's user-role filter, not an ARIA role
                      role="-user"
                      licenses="events"
                      placeholder="Select users"
                      predefinedOptions={[
                        { kind: PickerItemKind.User, name: 'Reporter\u2019s manager', uuid: MANAGER },
                      ]}
                      value={collaboratorPickerValue}
                      onChange={(next) => {
                        assignmentRules.updateRuleGeneric(rule.uuid, {
                          targetUsers: next
                            .filter((option) => option.kind === PickerItemKind.User)
                            .map(({ uuid, name }) => ({ uuid, name })),
                          targetGroups: next
                            .filter((option) => option.kind === PickerItemKind.Group)
                            .map(({ uuid, name, memberCount }) => ({ uuid, name, memberCount })),
                        });
                      }}
                    />
                    <GroupLicenceNotice value={collaboratorPickerValue} />
                  </>
                ) : (
                  <>
                    <FieldLabel>Assign event to pipeline</FieldLabel>
                    {/* Pipeline picker */}
                    <PipelinePicker
                      fullWidth
                      placeholder="Choose an option"
                      value={(rule as AssignmentRulePipelineResponseType).pipeline || null}
                      onChange={(value) => {
                        assignmentRules.updateRuleGeneric(rule.uuid, {
                          pipeline: value ? { uuid: value.uuid, name: value.name } : undefined,
                        });
                      }}
                      customHeight="56px"
                    />
                  </>
                )}
              </Box>
            </ConditionContainer>
          </AssignmentRuleContainer>
        </Collapse>
      </SectionContainer>
    </SectionTooltipAction>
  );
};

export default observer(AssignmentRule);
